/**
 * EndConfirmScreen — Hold-to-confirm pattern.
 *
 * UI-INV-005: Leaf component, props only.
 * UI-INV-009: Local state is permitted for simple flags (animation).
 */
import { useState, useEffect, useRef } from "react";
import styles from "./EndConfirmScreen.module.css";
import { SecondaryAction } from "../primitives/SecondaryAction";
import { TIMING } from "../constants";
import { TEST_IDS } from "../testing/testIds";
import { calculateCircumference } from "../utils/geometry";

type Props = {
  onConfirm: () => void;
  onCancel: () => void;
};

const HOLD_DURATION_MS = TIMING.END_CONFIRM_HOLD_MS;
const RADIUS = 64;
const CIRCUMFERENCE = calculateCircumference(RADIUS);

export function EndConfirmScreen({ onConfirm, onCancel }: Props) {
  const [progress, setProgress] = useState(0); // 0.0 to 1.0
  const [isHolding, setIsHolding] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  // Animation loop
  useEffect(() => {
    if (isHolding) {
      startTimeRef.current = performance.now() - progress * HOLD_DURATION_MS;

      const tick = (now: number) => {
        const elapsed = now - startTimeRef.current;
        const newProgress = Math.min(1, elapsed / HOLD_DURATION_MS);
        setProgress(newProgress);

        if (newProgress >= 1) {
          setIsHolding(false);
          if (typeof navigator !== "undefined" && navigator.vibrate) {
            navigator.vibrate(160);
          }
          onConfirm();
        } else {
          rafRef.current = requestAnimationFrame(tick);
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    } else {
      // Release: rapidly reset progress to 0
      if (progress > 0) {
        const resetTick = () => {
          setProgress((prev) => {
            const next = Math.max(0, prev - 0.1);
            if (next > 0) {
              rafRef.current = requestAnimationFrame(resetTick);
            }
            return next;
          });
        };
        rafRef.current = requestAnimationFrame(resetTick);
      }
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isHolding, onConfirm, progress]);

  const startHold = () => setIsHolding(true);
  const endHold = () => setIsHolding(false);

  return (
    <div className={styles.endConfirm} data-testid={TEST_IDS.END_CONFIRM_SCREEN}>
      <div className={styles.eyebrow}>This will lay the retreat down</div>
      <div className={styles.title}>Lay it down?</div>

      {/* The held ring — primary affordance */}
      <div
        className={styles.ringContainer}
        onPointerDown={startHold}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={endHold}
        onContextMenu={(e) => e.preventDefault()}
      >
        <svg width={140} height={140} aria-label={`Hold for ${HOLD_DURATION_MS / 1000} seconds to lay it down`}>
          <circle
            cx="70"
            cy="70"
            r={RADIUS}
            fill="none"
            stroke="var(--rc-rule-soft)"
            strokeWidth="1.4"
          />
          <circle
            cx="70"
            cy="70"
            r={RADIUS}
            fill="none"
            stroke="var(--rc-clay)"
            strokeWidth="2"
            strokeDasharray={`${CIRCUMFERENCE * progress} ${CIRCUMFERENCE}`}
            transform="rotate(-90 70 70)"
          />
        </svg>
        <div className={styles.ringLabel}>Hold</div>
      </div>

      <div className={styles.helpText}>{`hold for ${HOLD_DURATION_MS / 1000} seconds to lay it down`}</div>

      <div className={styles.actions}>
        <SecondaryAction onClick={onCancel} testId={TEST_IDS.END_CONFIRM_RETURN_BTN}>Return to the cushion</SecondaryAction>
      </div>
    </div>
  );
}
